/**
 * Layout component for the public portal.
 * Provides navigation, header, and footer for public-facing pages.
 *
 * Accessibility (RGAA AA):
 * - Skip link to main content
 * - Proper landmarks (banner, navigation, main, contentinfo)
 * - aria-current for active navigation links
 * - Accessible button labels
 * - Focus management and visible focus indicators
 * - Reduced motion support via Tailwind's motion-reduce
 */

import { useState, useCallback } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { usePatronAuth } from "../contexts/PatronAuthContext";

export function PublicLayout() {
  const { patron, isAuthenticated, logout } = usePatronAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const isActivePrefix = (prefix: string) => location.pathname.startsWith(prefix);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
      {/* Skip link - RGAA 12.7 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:rounded-lg focus:bg-blue-600 focus:px-4 focus:py-2 focus:text-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
      >
        Aller au contenu principal
      </a>

      {/* Header - role="banner" is implicit for <header> */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo / Brand */}
          <Link
            to="/opac"
            className="flex items-center gap-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
            aria-label="Bibliothèque - Accueil"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <span className="hidden text-xl font-semibold text-gray-900 sm:block dark:text-white">
              Bibliothèque
            </span>
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden items-center gap-1 md:flex" aria-label="Navigation principale">
            <Link
              to="/opac"
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none motion-reduce:transition-none ${
                isActive("/opac")
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
              aria-current={isActive("/opac") ? "page" : undefined}
            >
              Accueil
            </Link>
            <Link
              to="/opac/search"
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none motion-reduce:transition-none ${
                isActivePrefix("/opac/search")
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
              aria-current={isActivePrefix("/opac/search") ? "page" : undefined}
            >
              Recherche
            </Link>
            <Link
              to="/opac/new-arrivals"
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none motion-reduce:transition-none ${
                isActive("/opac/new-arrivals")
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
              aria-current={isActive("/opac/new-arrivals") ? "page" : undefined}
            >
              Nouveautés
            </Link>
            <Link
              to="/opac/ressources-numeriques"
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none motion-reduce:transition-none ${
                isActive("/opac/ressources-numeriques")
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
              aria-current={isActive("/opac/ressources-numeriques") ? "page" : undefined}
            >
              Ressources numériques
            </Link>
          </nav>

          {/* User actions */}
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              type="button"
              className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none md:hidden dark:text-gray-400 dark:hover:bg-gray-700"
              onClick={toggleMobileMenu}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                {mobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>

            {isAuthenticated && patron ? (
              <div className="flex items-center gap-2">
                <Link
                  to="/opac/account"
                  className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  aria-label={`Mon compte - ${patron.firstName} ${patron.lastName}`}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <span className="hidden sm:inline">{patron.firstName}</span>
                </Link>
                {patron.activeLoanCount > 0 && (
                  <Link
                    to="/opac/account"
                    className="relative rounded-lg p-2 text-gray-600 hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:text-gray-400 dark:hover:bg-gray-700"
                    aria-label={`Mes prêts en cours : ${String(patron.activeLoanCount)} document${patron.activeLoanCount > 1 ? "s" : ""}`}
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    <span
                      className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-medium text-white"
                      aria-hidden="true"
                    >
                      {patron.activeLoanCount}
                    </span>
                  </Link>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:text-gray-400 dark:hover:bg-gray-700"
                  aria-label="Se déconnecter"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <Link
                to="/opac/login"
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
                <span>Mon compte</span>
              </Link>
            )}
          </div>
        </div>

        {/* Mobile navigation */}
        <nav
          id="mobile-menu"
          className={`border-t border-gray-200 px-4 py-2 md:hidden dark:border-gray-700 ${
            mobileMenuOpen ? "block" : "hidden"
          }`}
          aria-label="Navigation mobile"
        >
          <ul className="flex flex-col gap-1" role="list">
            <li>
              <Link
                to="/opac"
                className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none motion-reduce:transition-none ${
                  isActive("/opac")
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
                aria-current={isActive("/opac") ? "page" : undefined}
                onClick={() => {
                  setMobileMenuOpen(false);
                }}
              >
                Accueil
              </Link>
            </li>
            <li>
              <Link
                to="/opac/search"
                className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none motion-reduce:transition-none ${
                  isActivePrefix("/opac/search")
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
                aria-current={isActivePrefix("/opac/search") ? "page" : undefined}
                onClick={() => {
                  setMobileMenuOpen(false);
                }}
              >
                Recherche
              </Link>
            </li>
            <li>
              <Link
                to="/opac/new-arrivals"
                className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none motion-reduce:transition-none ${
                  isActive("/opac/new-arrivals")
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
                aria-current={isActive("/opac/new-arrivals") ? "page" : undefined}
                onClick={() => {
                  setMobileMenuOpen(false);
                }}
              >
                Nouveautés
              </Link>
            </li>
            <li>
              <Link
                to="/opac/ressources-numeriques"
                className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none motion-reduce:transition-none ${
                  isActive("/opac/ressources-numeriques")
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
                aria-current={isActive("/opac/ressources-numeriques") ? "page" : undefined}
                onClick={() => {
                  setMobileMenuOpen(false);
                }}
              >
                Ressources numériques
              </Link>
            </li>
          </ul>
        </nav>
      </header>

      {/* Main content - landmark */}
      <main id="main-content" className="flex-1" tabIndex={-1}>
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>

      {/* Footer - role="contentinfo" is implicit for <footer> */}
      <footer className="border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {/* About */}
            <div>
              <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                La bibliothèque
              </h2>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400" role="list">
                <li>
                  <a
                    href="#"
                    className="rounded hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-blue-400"
                  >
                    Horaires d'ouverture
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="rounded hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-blue-400"
                  >
                    Nous contacter
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="rounded hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-blue-400"
                  >
                    Plan d'accès
                  </a>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">Services</h2>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400" role="list">
                <li>
                  <a
                    href="#"
                    className="rounded hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-blue-400"
                  >
                    S'inscrire
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="rounded hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-blue-400"
                  >
                    Règlement
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="rounded hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-blue-400"
                  >
                    Suggestion d'achat
                  </a>
                </li>
              </ul>
            </div>

            {/* Accessibility */}
            <div>
              <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                Accessibilité
              </h2>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400" role="list">
                <li>
                  <a
                    href="#"
                    className="rounded hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-blue-400"
                  >
                    Déclaration d'accessibilité
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="rounded hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-blue-400"
                  >
                    Plan du site
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                Informations légales
              </h2>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400" role="list">
                <li>
                  <Link
                    to="/opac/mentions-legales"
                    className="rounded hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-blue-400"
                  >
                    Mentions légales
                  </Link>
                </li>
                <li>
                  <Link
                    to="/opac/confidentialite"
                    className="rounded hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-blue-400"
                  >
                    Politique de confidentialité
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-200 pt-6 dark:border-gray-700">
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              © {new Date().getFullYear()} Bibliothèque. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PublicLayout;
